import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Search, BookOpen, List, Share2, Users, Sparkles } from 'lucide-react'
import { useBranding } from '../../contexts/BrandingContext'

const QUICK_LINKS = [
  {
    to: '/browse',
    icon: BookOpen,
    title: 'Browse by Subject',
    description: 'Explore resources organised by discipline taxonomy and field of research.',
  },
  {
    to: '/lists',
    icon: List,
    title: 'My Lists',
    description: 'Manage your curated reading lists and saved resource collections.',
  },
  {
    to: '/connections',
    icon: Share2,
    title: 'Connections Graph',
    description: 'Visualise cross-domain relationships between scholarly resources.',
  },
  {
    to: '/shared',
    icon: Users,
    title: 'Shared With Me',
    description: 'Access resources and lists shared with you by colleagues.',
  },
] as const

export default function Landing() {
  const navigate = useNavigate()
  const { branding } = useBranding()
  const [query, setQuery] = useState('')
  const [embeddingsMode, setEmbeddingsMode] = useState(false)

  function handleSearch() {
    const trimmed = query.trim()
    if (!trimmed) return
    const params = new URLSearchParams({
      q: trimmed,
      embeddings: String(embeddingsMode),
    })
    navigate(`/search?${params.toString()}`)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const institutionName = branding?.institution_name || branding?.header_text || null

  return (
    <div className="min-h-[calc(100vh-65px)] bg-gradient-to-b from-acad-50 to-white relative overflow-hidden">
      {/* Decorative SVG pattern */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg
          className="absolute top-0 right-0 w-[600px] h-[600px] text-acad-100 opacity-60"
          viewBox="0 0 600 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="400" cy="100" r="80" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
          <circle cx="500" cy="250" r="50" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
          <rect x="350" y="300" width="60" height="60" rx="8" stroke="currentColor" strokeWidth="1.5" opacity="0.25" transform="rotate(15 380 330)" />
          <polygon points="450,180 480,240 420,240" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.3" />
          <circle cx="300" cy="50" r="30" stroke="currentColor" strokeWidth="1" opacity="0.2" />
        </svg>
        <svg
          className="absolute bottom-0 left-0 w-[500px] h-[500px] text-acad-200 opacity-40"
          viewBox="0 0 500 500"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="100" cy="400" r="70" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
          <rect x="30" y="280" width="50" height="50" rx="6" stroke="currentColor" strokeWidth="1.5" opacity="0.2" transform="rotate(-10 55 305)" />
          <circle cx="200" cy="450" r="40" stroke="currentColor" strokeWidth="1" opacity="0.25" />
          <polygon points="150,350 170,390 130,390" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.2" />
        </svg>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center px-4 pt-16 pb-20 sm:pt-24">
        {/* Institution name */}
        {institutionName && (
          <p className="text-sm font-medium text-acad-600 tracking-wide uppercase mb-3">
            {institutionName}
          </p>
        )}

        {/* Main heading */}
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 text-center mb-3">
          Academic Discovery Portal
        </h1>
        <p className="text-lg text-gray-500 text-center mb-10 max-w-xl">
          Search across journals, videos, courses and more with AI-enhanced cross-domain discovery.
        </p>

        {/* Search bar */}
        <div className="w-full max-w-2xl">
          <div className="relative flex items-center">
            <Search className="absolute left-4 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search journals, resources, topics..."
              className="w-full pl-12 pr-28 py-4 text-lg border-2 border-gray-200 rounded-full bg-white shadow-sm focus:outline-none focus:border-acad-400 focus:ring-4 focus:ring-acad-100 transition placeholder-gray-400"
              autoFocus
            />
            <button
              onClick={handleSearch}
              className="absolute right-2 px-6 py-2.5 bg-acad-500 hover:bg-acad-600 active:bg-acad-700 text-white font-semibold rounded-full transition shadow-sm"
            >
              Search
            </button>
          </div>

          {/* Embeddings toggle */}
          <div className="flex items-center justify-center mt-3 gap-2">
            <label className="flex items-center gap-2 cursor-pointer select-none group">
              <input
                type="checkbox"
                checked={embeddingsMode}
                onChange={(e) => setEmbeddingsMode(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-9 h-5 bg-gray-200 peer-checked:bg-violet-500 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-violet-300">
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
              </div>
              <Sparkles className="w-4 h-4 text-violet-500 opacity-60 group-hover:opacity-100 transition" />
              <span className="text-sm text-gray-600 group-hover:text-gray-900 transition">
                Enhanced AI Search
              </span>
            </label>
          </div>
        </div>

        {/* Quick link cards */}
        <div className="mt-16 w-full max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-4">
          {QUICK_LINKS.map((link) => {
            const Icon = link.icon
            return (
              <Link
                key={link.to}
                to={link.to}
                className="group flex items-start gap-4 p-5 bg-white rounded-xl border border-acad-200 hover:border-acad-400 hover:shadow-lg transition-all duration-200"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-acad-50 group-hover:bg-acad-100 flex items-center justify-center transition">
                  <Icon className="w-5 h-5 text-acad-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-acad-600 transition">
                    {link.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">
                    {link.description}
                  </p>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
